import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faUser,
  faShieldAlt,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';

const DarbarLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    emailOrMobile: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // DEMO BYPASS: Automatically log in without checking credentials

      // Create demo token and user data
      const demoToken = 'darbar-demo-token-' + Math.random().toString(36).substring(2);
      const demoUser = {
        id: '123',
        name: 'Darbar Demo User',
        email: formData.emailOrMobile || 'darbar@example.com',
        mobile: formData.emailOrMobile || '9876543210'
      };

      // Store in localStorage
      localStorage.setItem('darbarToken', demoToken);
      localStorage.setItem('darbarUser', JSON.stringify(demoUser));

      // Show success message
      alert('Demo login successful! Redirecting to Home page.');

      // Navigate to home page
      navigate('/');
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 mt-16">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col lg:flex-row my-8">
        {/* Left Side - Branding */}
        <div className="hidden lg:block lg:w-5/12 bg-gradient-to-br from-blue-700 via-indigo-600 to-purple-900 relative overflow-hidden text-white">
          {/* Background Pattern Overlay */}
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '24px 24px' }}></div>

          {/* Animated Background Elements */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
              opacity: [0.05, 0.1, 0.05]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -mr-32 -mt-32"
          ></motion.div>

          <motion.div
            animate={{
              scale: [1, 1.5, 1],
              x: [0, 50, 0],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400 rounded-full blur-3xl -ml-20 -mb-20"
          ></motion.div>

          <div className="relative flex flex-col justify-between h-full p-12 z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
                <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse shadow-[0_0_8px_rgba(147,197,253,0.8)]"></div>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-100">Public Service Portal</span>
              </div>

              <div className="w-20 h-20 mb-8 bg-gradient-to-tr from-white/20 to-white/5 backdrop-blur-xl border border-white/30 rounded-[2rem] flex items-center justify-center shadow-2xl relative group overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <FontAwesomeIcon icon={faUser} className="text-3xl text-white drop-shadow-md group-hover:scale-110 transition-transform duration-500" />
              </div>

              <h2 className="text-4xl font-[1000] tracking-tight mb-4 leading-[1.1] drop-shadow-sm">
                Janseva <span className="text-blue-300">Darbar</span>
              </h2>
              <p className="text-blue-50/80 font-medium tracking-wide text-base max-w-xs leading-relaxed">
                Empowering citizens to connect with their community and make a positive difference through direct engagement.
              </p>
            </motion.div>

            <div className="space-y-6">
              <div className="space-y-4">
                {[
                  { icon: faUser, title: "Personal Dashboard", desc: "Your engagement hub", color: "emerald", delay: 0.1 },
                  { icon: faShieldAlt, title: "Secure Platform", desc: "Private & protected data", color: "blue", delay: 0.2 },
                  { icon: faChartLine, title: "Community Tracking", desc: "Monitor social progress", color: "purple", delay: 0.3 }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: item.delay, duration: 0.5 }}
                    className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 hover:border-white/20 rounded-2xl p-4 transition-all duration-300 cursor-default"
                  >
                    <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                      <FontAwesomeIcon icon={item.icon} className={`text-${item.color}-300 text-xl`} />
                    </div>
                    <div>
                      <span className="font-black tracking-wide text-sm block group-hover:text-blue-300 transition-colors uppercase">{item.title}</span>
                      <p className="text-[11px] text-blue-100/60 uppercase tracking-widest font-bold font-sans">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="pt-8 border-t border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3 text-blue-100/60">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-xs" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]">Verified Secure</span>
                </div>
                <div className="flex -space-x-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="w-6 h-6 rounded-full border-2 border-blue-800 bg-blue-600 flex items-center justify-center text-[8px] font-bold">
                      {i}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="lg:w-7/12 p-8">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-md mx-auto"
          >
            <div className="text-center lg:text-left mb-8 group">
              <h1 className="text-3xl font-[1000] text-slate-900 tracking-tighter mb-4 uppercase leading-[1.1]">
                Sign In <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-500 uppercase">Janseva Darbar</span>
              </h1>
              <div className="w-16 h-1.5 bg-blue-600 rounded-full mb-4 transition-all duration-500 group-hover:w-24"></div>
              <p className="text-sm font-bold text-slate-500 tracking-wide flex items-center gap-2">
                <span className="w-1 h-4 bg-blue-600 rounded-full"></span>
                Please sign in to access your portal
              </p>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-700 text-xs font-semibold flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse"></div>
                  Demo Mode: Click "Sign in" to bypass login
                </p>
              </div>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 text-red-500 p-3 rounded-lg text-sm text-center"
                >
                  {error}
                </motion.div>
              )}

              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="emailOrMobile"
                  required
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Email or Mobile Number"
                  value={formData.emailOrMobile}
                  onChange={handleChange}
                />
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faLock} className="text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <div
                  className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon
                    icon={showPassword ? faEyeSlash : faEye}
                    className="text-gray-400 hover:text-gray-600"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div>
                <div className="text-sm">
                  <Link to="/darbar-forget-password" className="text-blue-600 hover:text-blue-500">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>

              <div className="text-center text-sm">
                <span className="text-gray-600">Don't have an account? </span>
                <Link to="/darbar-register" className="text-blue-600 hover:text-blue-500 font-medium">
                  Sign up
                </Link>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DarbarLogin;
